import React, { useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { LineChart } from '@mui/x-charts/LineChart';

export function TrendSparkline({
  counts,
  height = 140,
}: {
  counts: { label: string; value: number }[];
  height?: number;
}) {
  const hasData = counts.some((d) => d.value > 0);

  if (!counts.length || !hasData) {
    return (
      <Box
        sx={{
          height,
          borderRadius: 2,
          bgcolor: '#EDF4F6',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
          Veri yok
        </Typography>
      </Box>
    );
  }

  const xLabels = counts.map((d) => d.label);
  const data = counts.map((d) => d.value);

  return (
    <Box sx={{ height, width: '100%' }}>
      <LineChart
        xAxis={[{ 
            data: xLabels, 
            scaleType: 'point', 
            tickLabelStyle: { fontSize: 10, fill: '#3A6070', fontWeight: 600 } 
        }]}
        yAxis={[{ 
            min: 0, 
            tickMinStep: 1, 
            tickLabelStyle: { fontSize: 10, fill: '#3A6070', fontWeight: 600 } 
        }]}
        series={[
          {
            data,
            area: true,
            baseline: 'min',
            color: '#1A6070',
            showMark: ({ index }) => data[index] > 0,
          },
        ]}
        margin={{ left: 35, right: 15, top: 15, bottom: 25 }}
        slotProps={{
            popper: {
                modifiers: [
                    { name: 'preventOverflow', options: { padding: 10 } }
                ]
            }
        }}
      />
    </Box>
  );
}

function polarToCartesian(cx: number, cy: number, r: number, angle: number) {
  const rad = ((angle - 90) * Math.PI) / 180.0;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function sectorPath(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  if (endAngle - startAngle >= 359.99) {
    return [
      `M ${cx} ${cy - r}`,
      `A ${r} ${r} 0 1 1 ${cx - 0.001} ${cy - r}`,
      `Z`,
    ].join(' ');
  }
  const start = polarToCartesian(cx, cy, r, endAngle);
  const end = polarToCartesian(cx, cy, r, startAngle);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${cx} ${cy} L ${start.x} ${start.y} A ${r} ${r} 0 ${largeArcFlag} 0 ${end.x} ${end.y} Z`;
}

function labelPosition(cx: number, cy: number, r: number, startAngle: number, endAngle: number) {
  const midAngle = (startAngle + endAngle) / 2;
  return polarToCartesian(cx, cy, r * 0.65, midAngle);
}

export function PieChart({
  total,
  slices,
  size = 100,
}: {
  total: number;
  slices: Array<{ label: string; value: number; color: string }>;
  size?: number;
}) {
  const r = (size - 4) / 2;
  const cx = size / 2;
  const cy = size / 2;

  const sectors = useMemo(() => {
    if (!total) return [];
    let acc = 0;
    return slices
      .filter((s) => s.value > 0)
      .map((s) => {
        const start = acc;
        const sweep = (s.value / total) * 360;
        const end = start + sweep;
        acc = end;
        const pos = labelPosition(cx, cy, r, start, end);
        return { ...s, startAngle: start, endAngle: end, labelX: pos.x, labelY: pos.y, sweep };
      });
  }, [slices, total, cx, cy, r]);

  return (
    <Box sx={{ width: size, height: size, position: 'relative', flexShrink: 0 }}>
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label="Olay türleri dağılımı">
        {sectors.map((s) => (
          <path
            key={s.label}
            d={sectorPath(cx, cy, r, s.startAngle, s.endAngle)}
            fill={s.color}
            stroke="white"
            strokeWidth={1.5}
          />
        ))}
        {sectors.map((s) =>
          s.sweep > 20 ? (
            <text
              key={`label-${s.label}`}
              x={s.labelX}
              y={s.labelY}
              textAnchor="middle"
              dominantBaseline="central"
              fill="white"
              fontSize={s.sweep > 40 ? 11 : 9}
              fontWeight={700}
            >
              {s.value}
            </text>
          ) : null
        )}
      </svg>
    </Box>
  );
}
